import type { ThemeMode } from '../theme/createPaletteBridge'

export type ThemePreference = 'light' | 'dark' | 'system'

export const DEFAULT_THEME_PREFERENCE_STORAGE_KEY = 'kuramaThemePreference:v1'

export const DEFAULT_THEME_PREFERENCE: ThemePreference = 'light'

export const THEME_PREFERENCES: ThemePreference[] = ['light', 'dark', 'system']

export const isThemePreference = (value: string | null): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system'

export const readStoredThemePreference = (
  stored: string | null,
  fallback: ThemePreference = DEFAULT_THEME_PREFERENCE
): ThemePreference => (isThemePreference(stored) ? stored : fallback)

export const getSystemTheme = (): ThemeMode =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export const resolveThemeMode = (preference: ThemePreference): ThemeMode =>
  preference === 'system' ? getSystemTheme() : preference

export const getStoredThemePreference = (storageKey: string): ThemePreference | null => {
  if (typeof window === 'undefined') {
    return null
  }

  const stored = window.localStorage.getItem(storageKey)

  return isThemePreference(stored) ? stored : null
}

/** Stored preference wins; otherwise system when switching is enabled, else light. */
export const resolveThemePreference = (
  storageKey: string,
  isThemeSwitchingEnabled: boolean,
  defaultWhenEmptyAndEnabled: ThemePreference = 'system'
): ThemePreference => {
  const stored = getStoredThemePreference(storageKey)

  if (stored != null) {
    return stored
  }

  if (isThemeSwitchingEnabled) {
    return defaultWhenEmptyAndEnabled
  }

  return DEFAULT_THEME_PREFERENCE
}

export const readThemePreference = (storageKey: string, fallback: ThemePreference): ThemePreference => {
  if (typeof window === 'undefined') {
    return fallback
  }

  return readStoredThemePreference(window.localStorage.getItem(storageKey), fallback)
}

export const writeThemePreference = (storageKey: string, preference: ThemePreference): void => {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(storageKey, preference)
  }
}
